import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { format } from "date-fns"
import { CalendarIcon, Clock, Search, X } from "lucide-react"
import * as React from "react"
import type { DateRange } from "react-day-picker"
import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover"
import { OrdersAdminGrid } from "@/features/orders-admin/OrdersAdminGrid"
import type { OrdersAdminInput } from "@/features/orders-admin/orders-admin-datasource"
import { useDebounce } from "@/hooks/use-debounce"
import type { AdminOrderFiltersInput } from "@/types/marketplace.types"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/orders/")({
	component: OrdersPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePositiveInt(search.page),
		// Column-managed filters (CSV-encoded values, owned by the grid).
		status: search.status as string | undefined,
		region: search.region as string | undefined,
		dispute: search.dispute as string | undefined,
		riskLevel: search.riskLevel as string | undefined,
		shipping: search.shipping as string | undefined,
		// Non-column filters
		needsAction: search.needsAction as string | undefined,
		reviewStatus: search.reviewStatus as string | undefined,
		minDaysInStatus: normalizePositiveInt(search.minDaysInStatus),
		search: search.search as string | undefined,
		dateFrom: normalizeDateString(search.dateFrom),
		dateTo: normalizeDateString(search.dateTo),
		sortBy: search.sortBy as string | undefined,
		sortOrder: search.sortOrder as string | undefined,
	}),
})

/**
 * Coerce `?page=` / `?minDaysInStatus=` to a positive integer; anything
 * malformed (negative, decimal, NaN) becomes `undefined` so we fall back to
 * page 1 / no staleness filter.
 */
function normalizePositiveInt(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const i = Math.floor(n)
	return i > 0 ? i : undefined
}

/**
 * Coerce `?dateFrom=` / `?dateTo=` to a string only when it parses to a
 * real date. A malformed value otherwise reaches `format(...)` downstream
 * and throws on `Invalid Date`.
 */
function normalizeDateString(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined
	const d = new Date(raw)
	return Number.isNaN(d.getTime()) ? undefined : raw
}

/**
 * Isolated search input: manages its own local state so keystrokes don't
 * re-render the entire OrdersPage and grid.
 */
function DebouncedSearchInput({
	defaultValue,
	onDebouncedChange,
	inputRef,
}: {
	defaultValue: string
	onDebouncedChange: (value: string) => void
	inputRef?: React.RefObject<{ clear: () => void } | null>
}) {
	const [value, setValue] = React.useState(defaultValue)
	const debouncedValue = useDebounce(value, 150)
	const isInitialMount = React.useRef(true)

	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
			return
		}
		onDebouncedChange(debouncedValue)
	}, [debouncedValue, onDebouncedChange])

	React.useImperativeHandle(inputRef, () => ({
		clear: () => setValue(""),
	}))

	return (
		<div className="relative w-full min-w-[200px] max-w-[300px] flex-1">
			<Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-meta" />
			<Input
				placeholder="Order, @buyer or @seller"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="pl-8"
			/>
		</div>
	)
}

/**
 * Compact "days in current status" input. Same isolation as the search input,
 * plus an imperative `set` so the presets and Clear can push their value back
 * into it.
 */
function DebouncedDaysInput({
	defaultValue,
	onDebouncedChange,
	inputRef,
}: {
	defaultValue: string
	onDebouncedChange: (days: number | undefined) => void
	inputRef?: React.RefObject<{ set: (value: string) => void } | null>
}) {
	const [value, setValue] = React.useState(defaultValue)
	const debouncedValue = useDebounce(value, 150)
	const isInitialMount = React.useRef(true)

	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
			return
		}
		const days = Number.parseInt(debouncedValue, 10)
		onDebouncedChange(days > 0 ? days : undefined)
	}, [debouncedValue, onDebouncedChange])

	React.useImperativeHandle(inputRef, () => ({
		set: (next: string) => setValue(next),
	}))

	return (
		<div className="flex items-center justify-between gap-3 text-sm">
			<label htmlFor="orders-stale-days" className="text-muted-foreground">
				Days in current status, at least
			</label>
			<Input
				id="orders-stale-days"
				type="number"
				min={1}
				inputMode="numeric"
				aria-label="Minimum days in current status"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="w-16 tabular-nums"
			/>
		</div>
	)
}

/**
 * One toggle of the quick filter group: grey text, the surface fill and a
 * hairline when pressed.
 */
function QuickFilter({
	active,
	onClick,
	children,
}: {
	active: boolean
	onClick: () => void
	children: React.ReactNode
}) {
	return (
		<button
			type="button"
			aria-pressed={active}
			onClick={onClick}
			className={cn(
				"inline-flex h-[26px] cursor-pointer items-center whitespace-nowrap rounded-[4px] px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
				active && "bg-card text-foreground shadow-[0_0_0_1px_var(--border)]",
			)}
		>
			{children}
		</button>
	)
}

const STALE_PRESETS = [
	{ status: "shipped", days: 14, label: "Shipped over 14 days ago" },
	{ status: "delivered", days: 7, label: "Delivered over 7 days ago" },
] as const

function OrdersPage() {
	const searchParams = useSearch({ from: "/orders/" })
	const navigate = useNavigate()
	const [total, setTotal] = React.useState<number | undefined>(undefined)

	const pageIndex = (searchParams.page ?? 1) - 1
	const reviewStatusFilter = (searchParams.reviewStatus ?? "all") as
		| "needs_review"
		| "reviewed"
		| "all"
	const statusFilter = searchParams.status ?? ""
	const disputeFilter = searchParams.dispute ?? ""

	const dateRange: DateRange | undefined = React.useMemo(() => {
		if (!searchParams.dateFrom && !searchParams.dateTo) return undefined
		return {
			from: searchParams.dateFrom ? new Date(searchParams.dateFrom) : undefined,
			to: searchParams.dateTo ? new Date(searchParams.dateTo) : undefined,
		}
	}, [searchParams.dateFrom, searchParams.dateTo])

	const searchRef = React.useRef<{ clear: () => void }>(null)
	const staleDaysRef = React.useRef<{ set: (value: string) => void }>(null)

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/orders",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === "all" || val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) => {
			updateFilters({ search: value || undefined, page: undefined })
		},
		[updateFilters],
	)

	const handleDebouncedDays = React.useCallback(
		(days: number | undefined) => {
			updateFilters({ minDaysInStatus: days, page: undefined })
		},
		[updateFilters],
	)

	const sortBy = (searchParams.sortBy ??
		"createdAt") as AdminOrderFiltersInput["sortBy"]
	const sortOrder = (searchParams.sortOrder ??
		"desc") as AdminOrderFiltersInput["sortOrder"]

	// Only non-column filters reach the grid through `input`. The column
	// filters (status / region / riskLevel / dispute) are owned by the grid
	// and read from the URL via its own initialState + effect.
	const gridInput = React.useMemo<OrdersAdminInput>(() => {
		const filters: OrdersAdminInput = {
			sortBy,
			sortOrder,
		}
		if (searchParams.search) filters.search = searchParams.search
		if (reviewStatusFilter !== "all") filters.reviewStatus = reviewStatusFilter
		if (searchParams.needsAction === "true") filters.needsAction = true
		if (searchParams.dateFrom) filters.dateFrom = searchParams.dateFrom
		if (searchParams.dateTo) filters.dateTo = searchParams.dateTo
		if (searchParams.minDaysInStatus)
			filters.minDaysInStatus = searchParams.minDaysInStatus
		return filters
	}, [
		searchParams.search,
		searchParams.needsAction,
		searchParams.dateFrom,
		searchParams.dateTo,
		searchParams.minDaysInStatus,
		reviewStatusFilter,
		sortBy,
		sortOrder,
	])

	const handlePageChange = React.useCallback(
		(nextPageIndex: number) => {
			updateFilters({
				page: nextPageIndex > 0 ? nextPageIndex + 1 : undefined,
			})
		},
		[updateFilters],
	)

	const handleSortChange = React.useCallback(
		(
			nextSortBy: AdminOrderFiltersInput["sortBy"],
			nextSortOrder: AdminOrderFiltersInput["sortOrder"],
		) => {
			updateFilters({
				sortBy:
					nextSortBy === "createdAt" && nextSortOrder === "desc"
						? undefined
						: nextSortBy,
				sortOrder:
					nextSortBy === "createdAt" && nextSortOrder === "desc"
						? undefined
						: nextSortOrder,
				page: undefined,
			})
		},
		[updateFilters],
	)

	const clearFilters = React.useCallback(() => {
		searchRef.current?.clear()
		staleDaysRef.current?.set("")
		navigate({ to: "/orders", search: {} as never, replace: true })
	}, [navigate])

	const hasActiveFilters =
		!!searchParams.status ||
		!!searchParams.region ||
		!!searchParams.riskLevel ||
		!!searchParams.dispute ||
		!!searchParams.shipping ||
		!!searchParams.needsAction ||
		reviewStatusFilter !== "all" ||
		dateRange !== undefined ||
		!!searchParams.minDaysInStatus ||
		!!searchParams.search

	// Flips a CSV-encoded URL filter to contain just `value`, or clears it if
	// already set to that value.
	const toggleChip = React.useCallback(
		(key: "status" | "dispute" | "reviewStatus", value: string) => {
			const current = searchParams[key]
			updateFilters({
				[key]: current === value ? undefined : value,
				page: undefined,
			})
		},
		[searchParams, updateFilters],
	)

	// Staleness presets own two params at once: a preset is only active when
	// both match, and toggling it off clears both.
	const isStalePreset = React.useCallback(
		(status: string, days: number) =>
			statusFilter === status && searchParams.minDaysInStatus === days,
		[statusFilter, searchParams.minDaysInStatus],
	)

	const toggleStalePreset = React.useCallback(
		(status: string, days: number) => {
			const active = isStalePreset(status, days)
			staleDaysRef.current?.set(active ? "" : String(days))
			updateFilters({
				status: active ? undefined : status,
				minDaysInStatus: active ? undefined : days,
				page: undefined,
			})
		},
		[isStalePreset, updateFilters],
	)

	const staleDays = searchParams.minDaysInStatus

	const toolbar = (
		<>
			<DebouncedSearchInput
				defaultValue={searchParams.search ?? ""}
				onDebouncedChange={handleDebouncedSearch}
				inputRef={searchRef}
			/>

			<Popover>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						className={cn(!dateRange && "text-muted-foreground")}
					>
						<CalendarIcon />
						{dateRange?.from ? (
							dateRange.to ? (
								<>
									{format(dateRange.from, "LLL dd")} to{" "}
									{format(dateRange.to, "LLL dd")}
								</>
							) : (
								format(dateRange.from, "LLL dd, y")
							)
						) : (
							<span>Date range</span>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						initialFocus
						mode="range"
						defaultMonth={dateRange?.from}
						selected={dateRange}
						onSelect={(range) =>
							updateFilters({
								dateFrom: range?.from?.toISOString() || undefined,
								dateTo: range?.to?.toISOString() || undefined,
								page: undefined,
							})
						}
						numberOfMonths={2}
					/>
				</PopoverContent>
			</Popover>

			<fieldset
				aria-label="Quick filters"
				className="inline-flex h-[30px] shrink-0 items-center gap-0.5 rounded-md bg-muted p-[2px]"
			>
				<QuickFilter
					active={disputeFilter === "escalated"}
					onClick={() => toggleChip("dispute", "escalated")}
				>
					Escalated
				</QuickFilter>
				<QuickFilter
					active={searchParams.needsAction === "true"}
					onClick={() =>
						updateFilters({
							needsAction:
								searchParams.needsAction === "true" ? undefined : "true",
							page: undefined,
						})
					}
				>
					Needs action
				</QuickFilter>
				<QuickFilter
					active={statusFilter === "shipped"}
					onClick={() => toggleChip("status", "shipped")}
				>
					In transit
				</QuickFilter>
				<QuickFilter
					active={statusFilter === "pending_shipment"}
					onClick={() => toggleChip("status", "pending_shipment")}
				>
					Pending shipment
				</QuickFilter>
				<QuickFilter
					active={reviewStatusFilter === "needs_review"}
					onClick={() => toggleChip("reviewStatus", "needs_review")}
				>
					Needs review
				</QuickFilter>
			</fieldset>

			<Popover>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						className={cn(!staleDays && "text-muted-foreground")}
					>
						<Clock />
						{staleDays ? `Stale \u2265 ${staleDays} days` : "Stale"}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="flex w-72 flex-col gap-3 p-3" align="start">
					<div className="flex flex-col gap-1">
						{STALE_PRESETS.map((preset) => {
							const active = isStalePreset(preset.status, preset.days)
							return (
								<button
									key={preset.status}
									type="button"
									aria-pressed={active}
									onClick={() => toggleStalePreset(preset.status, preset.days)}
									className={cn(
										"flex h-[30px] cursor-pointer items-center rounded-md px-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
										active && "bg-accent text-foreground",
									)}
								>
									{preset.label}
								</button>
							)
						})}
					</div>
					<div className="border-t pt-3">
						<DebouncedDaysInput
							defaultValue={staleDays ? String(staleDays) : ""}
							onDebouncedChange={handleDebouncedDays}
							inputRef={staleDaysRef}
						/>
					</div>
				</PopoverContent>
			</Popover>

			{hasActiveFilters && (
				<Button variant="ghost" onClick={clearFilters}>
					<X />
					Clear
				</Button>
			)}
		</>
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Orders"
				count={total !== undefined ? total.toLocaleString("en-US") : undefined}
			/>

			<div className="min-h-0 flex-1">
				<OrdersAdminGrid
					input={gridInput}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					onSortChange={handleSortChange}
					onTotalChange={setTotal}
					toolbar={toolbar}
				/>
			</div>
		</div>
	)
}
